import React, { useState } from "react";
import styled from "styled-components";
import Header from "../components/Header";
import Layout from "../components/Layout";

const ContactContainer = styled.div``;

const Section = styled.section`
  padding: 5rem 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  color: #333;
`;

const Message = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  p {
    font-size: 1.2rem;
    line-height: 1.8;
    color: #666;
  }
`;

const FormContainer = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
  }

  input,
  textarea,
  select {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    color: #333;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #0090a0;
    }
  }

  textarea {
    min-height: 150px;
    resize: vertical;
  }

  select {
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 1em;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const SubmitButton = styled.button`
  display: block;
  width: 100%;
  padding: 1rem;
  background-color: #0090a0;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #007580;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ThankYouMessage = styled.div`
  text-align: center;
  padding: 2rem;

  h3 {
    font-size: 1.8rem;
    color: #0090a0;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.2rem;
    color: #666;
    line-height: 1.6;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "お名前を入力してください";
    }
    if (!formData.email.trim()) {
      newErrors.email = "メールアドレスを入力してください";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "有効なメールアドレスを入力してください";
    }
    if (!formData.subject) {
      newErrors.subject = "お問い合わせ内容を選択してください";
    }
    if (!formData.message.trim()) {
      newErrors.message = "メッセージを入力してください";
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);

      // メール送信用のデータを準備
      const mailtoLink = `mailto:info@technoroaig.com?subject=${encodeURIComponent(
        `【お問い合わせ】${formData.subject}`,
      )}&body=${encodeURIComponent(
        `お名前: ${formData.name}\n` +
          `メールアドレス: ${formData.email}\n` +
          `会社名: ${formData.company || "記入なし"}\n` +
          `電話番号: ${formData.phone || "記入なし"}\n` +
          `お問い合わせ内容: ${formData.subject}\n\n` +
          `${formData.message}`,
      )}`;

      // メーラーを開く
      window.open(mailtoLink, "_blank");

      // フォーム送信完了処理
      setTimeout(() => {
        setIsLoading(false);
        setIsSubmitted(true);
        // Reset form
        setFormData({
          name: "",
          email: "",
          company: "",
          phone: "",
          subject: "",
          message: "",
        });
      }, 1500);
    } else {
      setErrors(newErrors);
    }
  };

  return (
    <Layout>
      <ContactContainer>
        <Header
          title="お問い合わせ"
          subtitle="ご質問・ご相談はこちらから"
          showButton={false}
        />

        <Section>
          <SectionTitle data-aos="fade-down" data-aos-duration="500">
            お問い合わせフォーム
          </SectionTitle>
          <Message data-aos="fade-up" data-aos-duration="500">
            <p>
              サービスに関するご質問、お見積もりのご依頼、採用に関するお問い合わせなど、
              お気軽にご連絡ください。
            </p>
          </Message>

          <FormContainer data-aos="zoom-in" data-aos-duration="500">
            {isSubmitted ? (
              <ThankYouMessage data-aos="zoom-in" data-aos-duration="500">
                <h3>お問い合わせありがとうございます</h3>
                <p>メッセージを受け付けました。</p>
                <p>
                  メールが自動的に開かない場合はお手数ですが直接
                  info@technoroaig.com までご連絡ください。
                </p>
              </ThankYouMessage>
            ) : (
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <label htmlFor="name">お名前 *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                  {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="email">メールアドレス *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="company">会社名</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="phone">電話番号</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="subject">お問い合わせ内容 *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                  >
                    <option value="">選択してください</option>
                    <option value="service">サービスについて</option>
                    <option value="estimate">お見積もり依頼</option>
                    <option value="recruitment">採用について</option>
                    <option value="partnership">業務提携について</option>
                    <option value="other">その他</option>
                  </select>
                  {errors.subject && (
                    <ErrorMessage>{errors.subject}</ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="message">メッセージ *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                  ></textarea>
                  {errors.message && (
                    <ErrorMessage>{errors.message}</ErrorMessage>
                  )}
                </FormGroup>

                <SubmitButton type="submit" disabled={isLoading}>
                  {isLoading ? "送信中..." : "送信する"}
                </SubmitButton>
              </form>
            )}
          </FormContainer>
        </Section>
      </ContactContainer>
    </Layout>
  );
};

export default Contact;
